import { z } from 'zod';

interface DataItem {
  key: string;
  value: unknown;
}

const ItemSchema = z.object({
  key: z.string(),
  value: z.any(),
});

function collectStoredItems(): DataItem[] {
  const items: DataItem[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    if (!key.startsWith('demo_') && !key.startsWith('zippyhealth_')) continue;
    const raw = localStorage.getItem(key);
    if (!raw) continue;
    try {
      const parsed = JSON.parse(raw);
      if (
        parsed &&
        typeof parsed === 'object' &&
        'value' in parsed &&
        'timestamp' in parsed &&
        'version' in parsed
      ) {
        items.push({ key, value: (parsed as any).value });
      } else {
        items.push({ key, value: parsed });
      }
    } catch {
      items.push({ key, value: raw });
    }
  }
  return items;
}

export function exportStoredData(format: 'json' | 'csv'): string {
  const items = collectStoredItems();
  if (format === 'json') {
    return JSON.stringify(items, null, 2);
  }
  const rows = ['key,value'];
  for (const item of items) {
    const valueString =
      typeof item.value === 'string'
        ? item.value
        : JSON.stringify(item.value);
    const escaped = valueString.replace(/"/g, '""');
    rows.push(`"${item.key}","${escaped}"`);
  }
  return rows.join('\n');
}

function mergeValues(existing: unknown, incoming: unknown): unknown {
  if (Array.isArray(existing) && Array.isArray(incoming)) {
    const byId = new Map<string, any>();
    for (const item of existing) {
      if (item && typeof item === 'object' && 'id' in item) {
        byId.set((item as any).id, item);
      } else {
        byId.set(JSON.stringify(item), item);
      }
    }
    for (const item of incoming) {
      if (item && typeof item === 'object' && 'id' in item) {
        if (!byId.has((item as any).id)) byId.set((item as any).id, item);
      } else {
        const key = JSON.stringify(item);
        if (!byId.has(key)) byId.set(key, item);
      }
    }
    return Array.from(byId.values());
  }
  if (
    existing &&
    typeof existing === 'object' &&
    incoming &&
    typeof incoming === 'object' &&
    !Array.isArray(existing) &&
    !Array.isArray(incoming)
  ) {
    return { ...(existing as object), ...(incoming as object) };
  }
  return incoming;
}

export function importStoredData(
  data: string,
  format: 'json' | 'csv'
): { success: boolean; errors: string[] } {
  const errors: string[] = [];
  let items: DataItem[] = [];
  try {
    if (format === 'json') {
      const parsed = JSON.parse(data);
      if (!Array.isArray(parsed)) {
        throw new Error('JSON must be an array of {key, value}');
      }
      for (const item of parsed) {
        const result = ItemSchema.safeParse(item);
        if (result.success) {
          items.push(result.data);
        } else {
          errors.push('Invalid item structure');
        }
      }
    } else {
      const lines = data.split(/\r?\n/).filter(Boolean);
      for (const line of lines.slice(1)) {
        const csvRegex = /^"([^"]*)","((?:[^"]|"")*)"$/;
        const match = line.match(csvRegex);
        if (!match) {
          errors.push(`Malformed CSV line: ${line}`);
          continue;
        }
        const [, key, val] = match;
        try {
          items.push({ key, value: JSON.parse(val) });
        } catch {
          items.push({ key, value: val.replace(/""/g, '"') });
        }
      }
    }
  } catch (err: any) {
    return { success: false, errors: [err.message] };
  }

  for (const item of items) {
    try {
      const existingRaw = localStorage.getItem(item.key);
      if (existingRaw) {
        let existing: unknown = undefined;
        try {
          const parsed = JSON.parse(existingRaw);
          existing =
            parsed &&
            typeof parsed === 'object' &&
            'value' in parsed &&
            'timestamp' in parsed
              ? (parsed as any).value
              : parsed;
        } catch {
          existing = existingRaw;
        }
        const merged = mergeValues(existing, item.value);
        localStorage.setItem(item.key, JSON.stringify(merged));
      } else {
        localStorage.setItem(item.key, JSON.stringify(item.value));
      }
    } catch (err: any) {
      errors.push(`Failed to import key ${item.key}: ${err.message}`);
    }
  }

  return { success: errors.length === 0, errors };
}

